<template>
  <registration-layout
    ref="form"
  >
    <form
      class="new-registration vue"
      @submit.prevent="submitWrapper"
    >
      <section id="loan-offers-heading">
        <h2 id="loan-offers-title">
          {{ $t('pages.loan_offers.page_title') }}
        </h2>
        <p id="loan-offers-instructions">
          {{ $t('pages.loan_offers.page_subtitle') }}
        </p>
      </section>
      <div
        v-if="quotes.length > 0"
        id="loan-offers-body"
      >
        <section id="quote-slider-section">
          <quotes-slider
            :quotes="quotes"
            :installments="sessionQuote.installments_requested"
            :amount="parseFloat(sessionQuote.amount_requested)"
            @sliderChanged="sliderChanged"
          />
        </section>
        <section
          v-if="secondaryQuote"
          id="opt-in-card-section"
          ref="quoteSection"
          :class="secondaryQuote ? 'alternate-background-color' : ''"
        >
          <h3>{{ $t('pages.loan_offers.quote_cards_heading') }}</h3>
          <quote-cards
            ref="cards"
            :quotes="[primaryQuote, secondaryQuote]"
            :selected-quote-index="selectedQuoteIndex"
            :show-buttons="false"
            :show-carousel="showMobile"
            @quoteSelected="quoteSelected"
          >
            <template slot-scope="quote">
              <div class="opt-in-quote">
                <span class="first-installment-amount">
                  {{ presentableFirstInstallmentAmount(quote) }}
                </span>
                <span class="interest-rate">{{ presentableInterestRate(quote) }}</span>
                <p
                  v-if="quote.type === 'primary'"
                  v-html="$t('pages.loan_offers.primary_quote_description')"
                />
                <p
                  v-else
                  v-html="$t('pages.loan_offers.secondary_quote_description')"
                />
              </div>
            </template>
          </quote-cards>
        </section>
        <section
          v-if="selectedQuote"
          id="quote-details-section"
          :class="secondaryQuote ? '' : 'alternate-background-color'"
        >
          <h3
            v-if="secondaryQuote && selectedQuoteIndex === 0"
            class="loan-details-heading opted-in"
          >
            {{ $t('pages.loan_offers.loan_details_heading_yes_opt_in') }}
          </h3>
          <h3
            v-else-if="secondaryQuote && selectedQuoteIndex === 1"
            class="loan-details-heading opted-out"
          >
            {{ $t('pages.loan_offers.loan_details_heading_no_opt_in') }}
            <span class="tooltip-container">
              <brazil-v-tooltip
                tooltip-icon-id="opted-out-info-handle"
                type="info"
              >
                <span
                  v-html="$t('pages.loan_offers.loan_details_heading_no_opt_in_info')"
                />
              </brazil-v-tooltip>
            </span>
          </h3>
          <h3
            v-else
            class="loan-details-heading"
          >
            {{ $t('pages.loan_offers.loan_details_heading') }}
          </h3>
          <quote-details
            :quote="selectedQuote"
            :single-column="showMobile"
          />
          <div id="consent-and-submit">
            <brazil-checkbox-field
              v-if="overdraftCheckboxEnabled() && selectedQuoteIndex === 0 && secondaryQuote"
              name="overdraft_opt_in_consent"
              class="overdraft-opt-in-consent"
              input-id="overdraft_opt_in"
              :label="$t('pages.loan_offers.overdraft_consent_label')"
              :true-value="true"
              :false-value="false"
              :validator-field="$v.overdraft_opt_in"
            />
            <RegistrationNextButton
              ref="submitButton"
              :text="$t('pages.loan_offers.submit_text')"
              :use-spinner="true"
            />
          </div>
        </section>
      </div>
    </form>
    <brazil-v-dialog
      v-if="dialog"
      id="direct-debit-dialog"
      v-model="dialog"
      width="600px"
    >
      <div id="direct-debit-dialog-container">
        <p
          id="direct-debit-dialog-instructions"
          v-html="dialogBodyText"
        />
        <div class="button-container">
          <brazil-v-btn
            class="secondary-button"
            @click="submit"
          >
            {{ $t('pages.loan_offers.direct_debit_dialog_back_button_text') }}
          </brazil-v-btn>
          <brazil-v-btn
            class="primary-button"
            @click="nextButtonHandler"
          >
            {{ $t('pages.loan_offers.direct_debit_dialog_next_button_text') }}
          </brazil-v-btn>
        </div>
      </div>
    </brazil-v-dialog>
  </registration-layout>
</template>

<script>
import { call, get } from 'vuex-pathify';
import { currencyReals, interestRate } from '../filters';
import RegistrationLayout from '../layouts/RegistrationLayout.vue';
import QuotesSlider from '../components/QuoteSlider.vue';
import QuoteCards from '../components/QuoteCards.vue';
import BrazilVBtn from '../components/BrazilVBtn.vue';
import BrazilVDialog from '../components/BrazilVDialog.vue';
import BrazilVTooltip from '../components/BrazilVTooltip.vue';
import QuoteDetails from '../components/QuoteDetails.vue';
import RegistrationNextButton from '../components/RegistrationNextButton.vue';
import BrazilCheckboxField from '../components/fields/BrazilCheckboxField.vue';
import captureRenderBlockingException from '../tracking/helpers/captureRenderBlockingException';
import tagRecordings from '../tracking/hotjar/tagRecordings';
import { trackPageSubmit } from '../tracking/adobe';

export default {
  components: {
    // eslint-disable-next-line max-len
    RegistrationLayout,
    QuotesSlider,
    QuoteCards,
    QuoteDetails,
    BrazilCheckboxField,
    RegistrationNextButton,
    BrazilVBtn,
    BrazilVDialog,
    BrazilVTooltip,
  },
  data() {
    return {
      amountRequested:       '',
      installmentsRequested: 0,
      selectedQuoteIndex:    0, // index of filtered Quote. Set to 0 on any change.
      windowWidth:           window.innerWidth,
      dialog:                false,
    };
  },
  computed: {
    loanApplication: get('loanApplication'),
    sessionQuote:    get('sessionQuote'),
    refiMessage:     get('refiMessage'),
    quotes() {
      return this.lodash.sortBy(this.loanApplication.quotes, ['amount_requested', 'installments_requested']);
    },
    filteredQuotes() {
      const filterParams = {
        amount_requested:       this.amountRequested,
        installments_requested: this.installmentsRequested,
      };

      const quotes = this.lodash.filter(this.quotes, filterParams);
      return this.lodash.sortBy(quotes, 'interest_rate');
    },
    primaryQuote() {
      const primaryQuoteCopy = this.filteredQuotes[0];
      primaryQuoteCopy.type = 'primary';
      return primaryQuoteCopy;
    },
    secondaryQuote() {
      if (this.filteredQuotes.length > 1 && this.loanApplication.channel !== 'preapproved') {
        const secondaryQuoteCopy = this.filteredQuotes[1];
        secondaryQuoteCopy.type = 'secondary';
        return secondaryQuoteCopy;
      }
      return null;
    },
    selectedQuote() {
      return this.filteredQuotes[this.selectedQuoteIndex];
    },
    showMobile() {
      return this.windowWidth < 700;
    },
    dialogBodyText() {
      const rawText = this.$t('pages.loan_offers.direct_debit_dialog_body');
      return this.lodash.replace(rawText, '[XXX]', this.totalDifferential);
    },
    totalDifferential() {
      try {
        const secondaryAmount = this.secondaryQuote.repayment_total;
        const primaryAmount = this.primaryQuote.repayment_total;
        return currencyReals(secondaryAmount - primaryAmount);
      } catch (e) {
        const extras = {
          primaryQuote:    this.primaryQuote,
          secondaryQuote:  this.primaryQuote,
          loanApplication: this.loanApplication,
        };
        captureRenderBlockingException(e, extras);
        return NaN;
      }
    },
  },
  mounted() {
    try {
      this.$store.dispatch('registration/setCurrentStepNumber', 4);
      this.$nextTick(() => {
        window.addEventListener('resize', this.onResize());
      });
    } catch (e) {
      this.flash(this.$t('shared.generic_error'), 'error');
      captureRenderBlockingException(e);
    }

    if (this.loanApplication.channel === 'preapproved') {
      this.flash(this.$t('pages.loan_offers.preapproval_message'), 'success');
    }

    if (this.refiMessage) {
      this.flash(this.refiMessage, 'info');
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize());
  },
  methods: {
    quoteSelected(index) {
      this.selectedQuoteIndex = index;
    },
    sliderChanged(event) {
      this.amountRequested = event.amount.toString();
      this.installmentsRequested = event.installments;
    },
    presentableFirstInstallmentAmount(quote) {
      return currencyReals(quote.first_installment_amount);
    },
    presentableInterestRate(quote) {
      return interestRate(quote.interest_rate);
    },
    overdraftCheckboxEnabled() {
      return false; // feature flag
    },
    nextButtonHandler() {
      this.$refs.cards.quoteSelected(0);
      const scrollParams = { block: 'center', behavior: 'smooth' };
      this.$refs.cards.$el.scrollIntoView(scrollParams);
      this.dialog = false;
    },
    submitOffer: call('registration/submitOffer'),
    async submitWrapper() {
      if (this.selectedQuote === this.secondaryQuote) {
        tagRecordings(['direct_debit_dialog_opened']);
        this.dialog = true;
      } else {
        this.dialog = false;
        this.submit();
      }
    },
    async submit() {
      this.flash().destroyAll();
      this.dialog = false;
      await this.$store.dispatch('setAwaitingNavigation', true);
      if (this.$v.$invalid || this.$v.$pending) {
        this.$v.$touch();
        this.$refs.submitButton.shakeDisableButton();
      } else {
        try {
          const payload = {
            loan_offer_id: this.selectedQuote.loan_offer_id,
          };

          payload.overdraft_opt_in = (this.selectedQuote === this.primaryQuote);

          const response = await this.submitOffer(payload);
          if (response.status === 201) {
            trackPageSubmit('loanOffers');
            window.location = response.data.redirect;
            return;
          }

          if (response.data.redirect) {
            window.location = response.data.redirect;
            return;
          }

          await this.$store.dispatch('setAwaitingNavigation', false);
          this.flash(this.$t('shared.generic_error'), 'error');
        } catch (error) {
          await this.$store.dispatch('setAwaitingNavigation', false);
          this.flash(this.$t('shared.generic_error'), 'error');
          window.location = '/';
        }
      }
    },
    onResize() {
      return () => { this.windowWidth = window.innerWidth; };
    },
  },
  validations: {
    overdraft_opt_in: {
      required: false,
    },
  },
};
</script>

<style lang="scss" scoped>
  #nav-buttons {
    width: 100%;
    margin-top: 1rem;
  }
  .alternate-background-color {
    background-color: #f3f3f3;
  }
  #quote-slider-section {
    padding: 1rem;
    .quote-slider {
      width: 100%;
      box-shadow: 0px 3px 3px 1px rgba(0, 0, 0, 0.2);
      margin-bottom: 2rem;
    }
  }
  h3 {
    color: #048a05;
    margin-bottom: 1.5rem;
    text-align: center;
    font-size: 1.25rem;
    font-family: "Bree Serif", serif;
    font-weight: 400;
  }
  #consent-and-submit {
    margin-top: 3em;
    margin-bottom: 2em;
  }
  #submit-button {
    width: 100%;
    margin-top: 1rem;
    border-radius: 25px;
    font-size: 1rem;
    font-family: "Open Sans", "Helvetica Neue", "Helvetica", "Arial", sans-serif;
    font-weight: 600;
    min-height: 80px;
    .loader {
      width: 24px;
      height: 24px;
    }
  }
  #loan-offers-heading {
    padding: 0 1rem;
  }
  #loan-offers-title {
    font-size: 2rem;
    line-height: 1.1;
    font-family: "Bree Serif", serif;
    margin-bottom: 1.25rem;
    padding-top: 2rem;
    color: #048a05 !important;
  }
  #loan-offers-instructions {
    text-align: center;
    font-size: 1.4em;
    margin-bottom: 1rem;
    color: #333333;
  }
  #opt-in-card-section {
    padding: 2em 1rem;
    margin: 0 -4px 1rem -4px;
  }
  .opt-in-quote {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    font-weight: 400;
    text-align: center;
    margin: 0 auto;
    min-height: 225px;
    .first-installment-amount {
      font-weight: bold;
      font-size: 2.5em;
      color: #333333;
    }
    .interest-rate {
      margin-bottom: 1.5em;
      font-size: .8em;
      color: #333333;
    }
  }
  #quote-details-section {
    padding: 1rem;

    h3.loan-details-heading.opted-out {
      color: #e21111;
      border: 4px solid #e21111;
      border-radius: 1rem;
      padding: .5em;
      position: relative;
    }

    .tooltip-container {
      position: absolute;
      right: 3px;
      bottom: 3px;
    }
  }
  #direct-debit-dialog {
    font-size: 1.25em;
    #direct-debit-dialog-container {
      padding: 3em;
    }
    #direct-debit-dialog-instructions {
      margin-bottom: 2em;
    }
    .button-container {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      flex-direction: column;
      button {
        white-space: normal;
        display: block;
        height: auto;
        padding: 0.5em 1em;
        margin: 1em;
        text-transform: initial;
      }
      .secondary-button {
        background-color: white;
        border: 1px solid #048a05;
        color: #048a05;
      }
      .primary-button {
        background-color: rgb(245, 191, 5);
        color: #4a4a4a;
      }
    }
  }

  @media screen and (min-width: 700px) {
    .alternate-background-color {
      background-color: #fff;
    }
    #loan-offers-title {
      padding-top: 0;
      font-size: 2rem;
      padding-top: 1rem;
    }
    #loan-offers-instructions {
      font-size: 1.3em;
      margin-bottom: 2rem;
    }
    #loan-offers-body {
      display: grid;
      width: 100%;
      grid-template-columns: 55% 45%;
    }
    #opt-in-card-section {
      padding: 0;
      min-height: 300px;
    }
    #quote-details-section {
      padding: 0;
      grid-column-start: 2;

      .quote-table tr.highlighted {
        background-color: #f3f3f3;
        td {
          line-height: 2;
        }
      }

      #submit-button {
        border-radius: 75px;
      }
    }
    .opt-in-quote {
      margin-top: 1em;
      justify-content: flex-start;
      min-height: 206px;
      &:hover {
        cursor: pointer;
      }
      .first-installment-amount {
        font-size: 1.5em;
      }
      p {
        font-size: 13px;
        line-height: 1.5;
        color: #333333;
      }
      span {
        line-height: 1.5;
      }
    }
    .brazil-card {
      align-items: normal;
    }
    h3 {
      text-align: left;
      margin-bottom: 1rem;
    }
    .quote-slider {
      width: 100%;
      padding: 18px;
      box-shadow: 10px 3px 23px 2px rgba(0, 0, 0, 0.13);
      border-radius: 14px;
      border: 1px solid #c5c5c5;
    }
    #quote-slider-section {
      .quote-slider {
        width: 100%;
        padding: 18px;
        border-radius: 14px;
        border: 1px solid #c5c5c5;
      }
    }
    #loan-offers-heading {
      margin: 0 auto;
    }
    #direct-debit-dialog {
      #direct-debit-dialog-container {
        padding: 2.5em;
      }
      p {
        font-size: 1.25em;
      }
      .button-container {
        flex-direction: row;
        button {
          padding: 0.5em 1em;
          margin: 1em;
          width: 45%;
        }
      }
    }
  }
</style>
